import { Loader } from "@googlemaps/js-api-loader";
import { AutocompleteProvider } from "../../../types/enums";
import { LatLng } from "../../maps/map-provider.interface";
import {
    AddressSuggestOptions,
    AddressSuggestion,
    GeoAddress,
    IAddressSuggestProvider,
    IGeocodingProvider
} from "../address-search-provider.interface";
import { fetchJsonAsync, GeoSearchError } from "../geo-search-fetch";
import { toGeoAddress, toGoogleAddressComponents } from "./google-address-format";

const AUTOCOMPLETE_URL = "https://places.googleapis.com/v1/places:autocomplete";
const PLACE_DETAILS_URL = "https://places.googleapis.com/v1/places";
const BIAS_RADIUS_METERS = 20000;

/**
 * Places API (New) has no "address" collection — that was the legacy widget's vocabulary, and
 * sending it returns INVALID_ARGUMENT. These are the Table B primary types that make up a
 * deliverable address, and five is the maximum the API accepts.
 */
const ADDRESS_PRIMARY_TYPES = ["street_address", "premise", "subpremise", "route", "intersection"];

// Google drops a session a few minutes after the last keystroke; reusing a stale token bills
// per request, so an abandoned search must not bleed into the next one.
const SESSION_IDLE_TIMEOUT_MS = 180_000;

// Places API (New) requires a version 4 UUID, so the fallback builds one rather than any
// random string — some embedded webviews expose getRandomValues but not randomUUID.
function createSessionToken(): string {
    if (typeof crypto.randomUUID === "function")
        return crypto.randomUUID();

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

type PlacePrediction = {
    placeId?: string,
    text?: { text?: string }
};

type AutocompleteResponse = {
    suggestions?: Array<{ placePrediction?: PlacePrediction }>
};

type PlaceDetailsResponse = {
    formattedAddress?: string,
    location?: { latitude?: number, longitude?: number },
    addressComponents?: Array<{ types: string[], longText?: string, shortText?: string }>
};

/**
 * Autocomplete and place details go through Places API (New), which supports browser CORS.
 * Geocoding stays on the Maps JS geocoder — the Geocoding web service sends no CORS headers,
 * so calling it from a page fails regardless of key configuration.
 */
export class GoogleAddressProvider implements IAddressSuggestProvider, IGeocodingProvider {
    public readonly providerId = AutocompleteProvider.GOOGLE;

    private readonly _apiKey: string;
    private readonly _loader: Loader;
    private _geocoder: google.maps.Geocoder | null = null;
    private _sessionToken: string | null = null;
    private _sessionTouchedAt = 0;

    constructor(apiKey: string, loader: Loader) {
        this._apiKey = apiKey;
        this._loader = loader;
    }

    public async suggestAsync(
        query: string,
        options: AddressSuggestOptions,
        signal: AbortSignal
    ): Promise<AddressSuggestion[]> {
        const body: Record<string, unknown> = {
            input: query,
            includeQueryPredictions: false,
            sessionToken: this._getSessionToken()
        };

        // The store locator searches cities and postal codes too, so only the delivery-address
        // input narrows predictions to addresses.
        if (options.mode === "address")
            body.includedPrimaryTypes = ADDRESS_PRIMARY_TYPES;

        if (options.at) {
            body.locationBias = {
                circle: {
                    center: { latitude: options.at.lat, longitude: options.at.lng },
                    radius: BIAS_RADIUS_METERS
                }
            };
        }

        if (options.language)
            body.languageCode = options.language;

        if (options.countryCodes?.length)
            body.includedRegionCodes = options.countryCodes.map(code => code.toLowerCase());

        const response = await fetchJsonAsync<AutocompleteResponse>(this.providerId, AUTOCOMPLETE_URL, {
            method: "POST",
            signal,
            headers: {
                "Content-Type": "application/json",
                "X-Goog-Api-Key": this._apiKey,
                "X-Goog-FieldMask": "suggestions.placePrediction.placeId,suggestions.placePrediction.text"
            },
            body: JSON.stringify(body)
        });

        return (response.suggestions ?? [])
            .slice(0, options.limit)
            .map(suggestion => this._toSuggestion(suggestion.placePrediction))
            .filter((suggestion): suggestion is AddressSuggestion => suggestion !== null);
    }

    public async resolveAsync(suggestion: AddressSuggestion): Promise<GeoAddress | null> {
        const placeId = suggestion.providerRef;
        if (typeof placeId !== "string" || !placeId)
            return null;

        const url = new URL(`${PLACE_DETAILS_URL}/${encodeURIComponent(placeId)}`);
        if (this._sessionToken)
            url.searchParams.set("sessionToken", this._sessionToken);

        try {
            const place = await fetchJsonAsync<PlaceDetailsResponse>(this.providerId, url.toString(), {
                headers: {
                    "X-Goog-Api-Key": this._apiKey,
                    "X-Goog-FieldMask": "addressComponents,location,formattedAddress"
                }
            });

            const latitude = place.location?.latitude;
            const longitude = place.location?.longitude;
            if (latitude === undefined || longitude === undefined)
                return null;

            return toGeoAddress(
                { lat: latitude, lng: longitude },
                place.formattedAddress,
                place.addressComponents ?? []
            );
        }
        finally {
            // Place details closes the billing session whether or not it answered usefully.
            this._sessionToken = null;
        }
    }

    /**
     * Groups the keystrokes of one address search plus its place-details call into a single
     * billed session; without it Google charges per request for every letter typed.
     */
    private _getSessionToken(): string {
        const now = Date.now();

        if (!this._sessionToken || now - this._sessionTouchedAt > SESSION_IDLE_TIMEOUT_MS)
            this._sessionToken = createSessionToken();

        this._sessionTouchedAt = now;
        return this._sessionToken;
    }

    public async geocodeAsync(query: string): Promise<LatLng | null> {
        const trimmed = query.trim();
        if (!trimmed)
            return null;

        const results = await this._geocodeAsync({ address: trimmed });
        const location = results?.[0]?.geometry.location;
        if (!location)
            return null;

        return { lat: location.lat(), lng: location.lng() };
    }

    public async reverseGeocodeAsync(coords: LatLng): Promise<GeoAddress | null> {
        const results = await this._geocodeAsync({ location: coords });
        const best = results?.[0];
        if (!best)
            return null;

        return toGeoAddress(coords, best.formatted_address, toGoogleAddressComponents(best.address_components));
    }

    private _toSuggestion(prediction: PlacePrediction | undefined): AddressSuggestion | null {
        const label = prediction?.text?.text;
        if (!label || !prediction?.placeId)
            return null;

        return {
            label,
            providerId: this.providerId,
            providerRef: prediction.placeId
        };
    }

    /**
     * ZERO_RESULTS means the address does not exist and resolves to null; every other non-OK
     * status is a fault the failover chain should act on.
     */
    private async _geocodeAsync(
        request: google.maps.GeocoderRequest
    ): Promise<google.maps.GeocoderResult[] | null> {
        await this._loader.importLibrary("geocoding");
        this._geocoder ??= new google.maps.Geocoder();

        return await new Promise<google.maps.GeocoderResult[] | null>((resolve, reject) => {
            this._geocoder!.geocode(request, (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results?.length) {
                    resolve(results);
                    return;
                }

                if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
                    resolve(null);
                    return;
                }

                reject(new GeoSearchError(this.providerId, `Google geocoder returned ${status}`));
            });
        });
    }
}
